/**
 * Decide se a rota atual deve ser mantida fora dos buscadores.
 *
 * OS 0007/2026, Seção V, IV — meta robots.
 *
 * As listas vêm do próprio Volto (`nonContentRoutes` e `nonContentRoutesPublic`)
 * em vez de uma cópia mantida à mão: rota nova que o core ou um add-on registre
 * passa a ser coberta sozinha. O `mpf-slots-frontend`, por exemplo, registra
 * `/edit-slot/:id` em `nonContentRoutes`, e por isso ela já entra aqui.
 */

import { isCmsUi } from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';

export interface RobotsContext {
  pathname?: string;
  /** `state.content.get.error.status` — 404 quando a página não existe. */
  contentErrorStatus?: number;
}

/**
 * Casa uma rota do Volto com o caminho atual.
 *
 * O `isCmsUi` do core faz `new RegExp(route).test(path)`, o que casa a rota em
 * QUALQUER posição — `/search` bateria numa página real chamada
 * `/atuacao/search-e-apreensao`. É um problema conhecido do core (ver o
 * WARNING em `helpers/Url/Url.js`). Aqui um falso positivo tiraria uma página
 * de conteúdo do índice, então a comparação é ancorada no fim do caminho, que
 * é a semântica documentada dessas listas ("'/add' casa '/foo/bar/add'").
 */
const matchesRoute = (pathname: string, route: string | RegExp): boolean =>
  route instanceof RegExp
    ? route.test(pathname)
    : pathname === route || pathname.endsWith(route);

export const shouldNotIndex = ({
  pathname,
  contentErrorStatus,
}: RobotsContext): boolean => {
  if (contentErrorStatus === 404) return true;

  const path = (pathname ?? '').replace(/\?.*$/, '');
  if (!path) return false;

  // Telas de edição e administração: /edit, /contents, /add, /controlpanel,
  // /edit-slot/... O `isCmsUi` exclui de propósito as rotas públicas, que a
  // linha seguinte cobre.
  if (isCmsUi(path)) return true;

  const publicRoutes: (string | RegExp)[] =
    config.settings?.nonContentRoutesPublic ?? [];
  return publicRoutes.some((route) => matchesRoute(path, route));
};

export default shouldNotIndex;
